/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    HIDE_FILTERS_TRUE_EMPTY_INVENTORY
 * Date:         2026-01-29
 * Modified By:  Cursor
 *
 * Description:
 * Hide the inventory Filters sidebar when the user has zero inventory records total
 * (true empty state). Keep filters visible when the user searches/filters down to 0 results.
 *
 * File Size Check:
 * - Current: 245 lines
 * - Adding: ~25 lines
 * - Projected: ~270 lines
 * - Decision: Proceed (targeted change; follow-up split recommended)
 *
 * Typography Compliance (UI files only):
 * - Utilities used: N/A (no typography changes)
 * - Tokens used: N/A
 *
 * Related Files:
 * - client/src/features/inventory/InventoryPage.tsx
 * - client/src/features/inventory/inventory-page/hooks/useInventoryState.ts
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    TOAST_MSG_UPDATE_IRIT_2026
 * Date:         2026-01-27
 * Modified By:  Cursor
 *
 * Description:
 * Updated Toastify messages to Irit's improved user-friendly versions.
 *
 * File Size Check:
 * - Current: ~225 lines
 * - Adding: ~4 lines
 * - Decision: Proceed
 *
 * Typography Compliance: N/A (notification messages only)
 * Related Files: utils/notifications.ts, inventory hooks
 */

/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    INVENTORY_PAGE_SPLIT_MAIN
 * Date:         2025-12-30
 * Modified By:  Cursor
 *
 * Description:
 * Refactored InventoryPage.tsx from 946 lines to <200 lines by
 * extracting hooks, utilities, and sub-components. Main component now acts
 * as orchestrator only. Split into:
 * - hooks/useInventoryState.ts (state, <100 lines)
 * - hooks/useInventoryActions.ts (actions, <100 lines)
 * - hooks/useInventoryUpload.ts (upload, <100 lines)
 * - utils/inventoryHelpers.ts (utilities, <150 lines)
 * - components/*.tsx (UI sections, <200 lines each)
 *
 * File Size Check:
 * - Original: 946 lines (373% over limit)
 * - Projected: ~180 lines
 * - Decision: Split completed (mandatory)
 */

import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { notify } from "../../utils/notifications";
import {
  getInventoryItem,
  confirmInventory,
  requestExport,
} from "../../services/inventoryService";
// [2026-02-08] Export polling: use context for app-level export state
import { useExport } from "../../contexts/ExportContext";
import { getUserProfile } from "../../services/usersService";
import { Inventory } from "../../interfaces/Inventory";
import { User } from "../../interfaces/User";
import { InventoryFilters as InventoryFiltersComponent } from "../../components/inventory/InventoryFilters";
import { InventoryItemCard } from "../../components/inventory/InventoryItemCard";
import { InventoryGrid } from "../../components/inventory/InventoryGrid";
import { PaginationControls } from "../../components/ui/PaginationControls";
import EditInventoryModal from "./EditInventoryModal";
import EmptyInventoryState from "./components/empty-state/EmptyInventoryState";
import {
  downloadInventoryTemplateCSV,
  downloadInventoryTemplateXLSX,
} from "../../utils/exportFile";

import { useInventoryState } from "./inventory-page/hooks/useInventoryState";
import { useInventoryActions } from "./inventory-page/hooks/useInventoryActions";
import { useInventoryUpload } from "./inventory-page/hooks/useInventoryUpload";
import InventoryHeader from "./inventory-page/components/InventoryHeader";
// [2026-09-15] UPLOAD_ERROR_VISIBILITY: previously orphaned - now mounted after each upload
import UploadStatusPanel from "./UploadStatusPanel";
import InventoryUploadModals from "./inventory-page/components/InventoryUploadModals";
import InventoryDeleteModal from "./inventory-page/components/InventoryDeleteModal";
// [2026-02-07] style-extraction: moved inline styles to SCSS module
import styles from "./Inventory.module.scss";
// [2026-02-09] Mobile: FilterDrawer for mobile filter sidebar
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { FilterDrawer } from "../../components/ui/FilterDrawer";
// [2026-02-10] Mobile: MobileInventoryCard for responsive card list
import { MobileInventoryCard } from "./components/MobileInventoryCard";

const InventoryPage: React.FC = () => {
  const navigate = useNavigate();
  // [2026-02-08] BR-16: Read URL params for action=upload from email links
  const [searchParams, setSearchParams] = useSearchParams();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadButtonRef = useRef<HTMLButtonElement | null>(null);
  // [2026-02-09] Mobile: Responsive detection and filter drawer state
  const { isMobile } = useMediaQuery();
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  const [profile, setProfile] = useState<User | null>(null);
  // [2026-02-08] Export polling: use context for app-level export state
  const { isExporting, startExport } = useExport();
  const [debouncedQ, setDebouncedQ] = useState("");
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editInitial, setEditInitial] = useState<any | null>(null);
  const [isDeletingId, setIsDeletingId] = useState<string | null>(null);
  const [deleteConfirmItem, setDeleteConfirmItem] = useState<Inventory | null>(null);

  const { state, setState, filteredInventoryItems } = useInventoryState();
  const { fetchItems, handleFilterChange, handleClearFilters, handleDeleteConfirm } =
    useInventoryActions(state, setState, debouncedQ);
  const {
    isUploading,
    showUploadConfirm,
    handleFilePicked,
    confirmUpload,
    cancelUpload,
    // [2026-09-15] UPLOAD_ERROR_VISIBILITY: live status + failure summary in-app
    activeJobId,
    sendProgress,
    clearActiveJob,
    stopActiveJob,
  } = useInventoryUpload();

  useEffect(() => {
    getUserProfile()
      .then((res) => setProfile(res.data as User))
      .catch((err) => console.warn("Failed to fetch profile", err));
  }, []);

  // [2026-02-08] BR-16: Auto-trigger upload dialog when action=upload param is present (from email link)
  useEffect(() => {
    if (searchParams.get("action") === "upload") {
      // Small delay to ensure fileInputRef is mounted
      const timer = setTimeout(() => {
        fileInputRef.current?.click();
        // Clear the param so it doesn't re-trigger on navigation
        setSearchParams({}, { replace: true });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [searchParams, setSearchParams]);

  // [2026-02-07] BR-16: Confirm inventory handler
  const handleConfirmInventory = async () => {
    try {
      await confirmInventory();
      notify.success("Inventory confirmed successfully");
      // Refresh profile to get updated confirmedAt
      getUserProfile()
        .then((r) => setProfile(r.data as User))
        .catch(() => {});
    } catch (err) {
      notify.error(err, "Failed to confirm inventory");
    }
  };

  // [2026-02-08] BR-17: Start export using context (survives navigation)
  const handleExportCSV = async () => {
    try {
      const res = await requestExport();
      const jobId = res.data.jobId;
      startExport(jobId);
      notify.info("Export started — your file will download automatically when ready.");
    } catch (err) {
      notify.error(err, "Failed to start export");
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQ(state.q), 300);
    return () => clearTimeout(timer);
  }, [state.q]);

  useEffect(() => {
    fetchItems(state.page);
  }, [debouncedQ, state.partNumber, state.category, state.condition, state.country, state.manufacturer, state.minPrice, state.maxPrice, state.sort, state.itemsPerPage, state.page, fetchItems]);

  // [2026-02-12] keyword-search-fix: With server-side search via `q`, the
  // server already returns filtered/paginated results. Just pass them through.
  useEffect(() => {
    setState((prev) => ({
      ...prev,
      items: filteredInventoryItems,
    }));
  }, [filteredInventoryItems, setState]);

  // [2026-02-24] BR-18: async for exceljs-based XLSX template with dropdowns
  const handleDownloadTemplate = async (ext: "csv" | "xlsx") => {
    try {
      ext === "csv" ? downloadInventoryTemplateCSV() : await downloadInventoryTemplateXLSX();
      // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
      notify.info("Template download started");
    } catch (e: any) {
      // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
      notify.error(e, "We couldn't download the template. Please try again");
    }
  };

  // [2026-01-29] true-empty-filters: Only hide sidebar when no records exist at all
  const hasAnyInventoryFilterActive =
    !!(state.q && state.q.trim()) ||
    !!(state.partNumber && state.partNumber.trim()) ||
    !!state.category ||
    !!state.condition ||
    !!(state.country && state.country.trim()) ||
    !!(state.manufacturer && state.manufacturer.trim()) ||
    state.minPrice > 0 ||
    state.maxPrice > 0;

  const isTrulyEmptyInventory = !state.loading && state.total === 0 && !hasAnyInventoryFilterActive;

  // [2026-02-09] Mobile: Count active filters for badge
  const activeFilterCount = [
    state.q,
    state.partNumber,
    state.category,
    state.condition,
    state.country,
    state.manufacturer,
    state.minPrice > 0,
    state.maxPrice > 0,
  ].filter(Boolean).length;

  // [2026-02-09] Mobile: Filter content component (reused in sidebar and drawer)
  const filterContent = (
    <InventoryFiltersComponent
      {...state}
      searchQuery={state.q}
      partNumberFilter={state.partNumber}
      categoryFilter={state.category}
      conditionFilter={state.condition}
      countryFilter={state.country}
      manufacturerFilter={state.manufacturer}
      minPriceFilter={state.minPrice}
      maxPriceFilter={state.maxPrice}
      onSearchChange={(v) => handleFilterChange("q", v)}
      onPartNumberChange={(v) => handleFilterChange("partNumber", v)}
      onCategoryChange={(v) => handleFilterChange("category", v)}
      onConditionChange={(v) => handleFilterChange("condition", v)}
      onCountryChange={(v) => handleFilterChange("country", v)}
      onManufacturerChange={(v) => handleFilterChange("manufacturer", v)}
      onMinPriceChange={(v) => handleFilterChange("minPrice", v)}
      onMaxPriceChange={(v) => handleFilterChange("maxPrice", v)}
      onClearFilters={handleClearFilters}
      allInventoryItems={state.allInventoryItems}
      collapsed={false}
      onToggleCollapse={() => {}}
    />
  );

  // [2026-09-16] An upload REPLACES the whole catalog, so whatever page the user was on
  // beforehand is a meaningless offset into brand new data - a 17,667-item upload left the
  // list sitting on "page 999 of 1767" showing rows 9981-9990. Snap back to page 1.
  const handleUploadFinished = React.useCallback(() => {
    setState((prev: any) => ({ ...prev, page: 1 }));
    fetchItems(1);
  }, [setState, fetchItems]);

  return (
    <div className={`container ${styles.fullWidthContainer}`}>
      <InventoryHeader
        profile={profile}
        onAddItem={() => { setEditInitial(null); setIsEditOpen(true); }}
        onUploadClick={() => fileInputRef.current?.click()}
        isUploading={isUploading}
        onDownloadTemplate={handleDownloadTemplate}
        sort={state.sort}
        onSortChange={(val) => handleFilterChange("sort", val)}
        fileInputRef={fileInputRef}
        handleFilePicked={(e) => handleFilePicked(e, fileInputRef, uploadButtonRef as any)}
        onConfirmInventory={handleConfirmInventory}
        onExportCSV={handleExportCSV}
        isExporting={isExporting}
        lastConfirmedAt={profile?.inventoryConfirmedAt || null}
        totalItems={state.total}
      />

      {/* [2026-09-15] UPLOAD_ERROR_VISIBILITY: live processing status + grouped failure
          summary for the current upload. Refreshes the list when the job finishes. */}
      {activeJobId && (
        <UploadStatusPanel
          jobId={activeJobId}
          sendProgress={sendProgress}
          onJobComplete={handleUploadFinished}
          onDismiss={clearActiveJob}
          onStop={stopActiveJob}
        />
      )}

      {/* [2026-09-16] While an upload runs the list below still shows the PREVIOUS catalog -
          it only refreshes on completion. Without this note a stale or partial catalog reads
          as the current one (a 17,667-row upload showed 97,296 items from the prior file). */}
      {activeJobId && (
        <div className="text-xs mb-2" style={{ color: "var(--color-text-muted, #6b7280)" }}>
          The list below is from your previous upload. It will refresh when this one finishes.
        </div>
      )}

      {/* [2026-02-10] Mobile: Sort+Filter toolbar and drawer */}
      {isMobile && !isTrulyEmptyInventory && (
        <>
          <div className="inventory-mobile-toolbar">
            <div className="inventory-mobile-toolbar__sort">
              <span>Sort:</span>
              <select
                value={state.sort}
                onChange={(e) => handleFilterChange("sort", e.target.value)}
                aria-label="Sort inventory items"
              >
                <option value="newest">Newest</option>
                <option value="price_desc">Price: High → Low</option>
                <option value="price_asc">Price: Low → High</option>
              </select>
            </div>
            <button
              className={`filter-button ${activeFilterCount > 0 ? "has-filters" : ""}`}
              onClick={() => setFilterDrawerOpen(true)}
            >
              <svg className="filter-button__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
              </svg>
              <span>Filters</span>
              {activeFilterCount > 0 && (
                <span className="filter-button__count">{activeFilterCount}</span>
              )}
            </button>
          </div>
          <FilterDrawer
            isOpen={filterDrawerOpen}
            onClose={() => setFilterDrawerOpen(false)}
            title="Filters"
            activeFilterCount={activeFilterCount}
            onClear={handleClearFilters}
            onApply={() => setFilterDrawerOpen(false)}
          >
            {filterContent}
          </FilterDrawer>
        </>
      )}

      <div className="row g-0">
        {/* [2026-02-09] Desktop: Sidebar filters (hidden on mobile) */}
        {!isTrulyEmptyInventory && !isMobile && (
          <aside className={`${state.filtersCollapsed ? "col-1" : "col-12 col-md-4 col-lg-3"} border-end bg-white p-3`}>
            <InventoryFiltersComponent
              {...state}
              searchQuery={state.q}
              partNumberFilter={state.partNumber}
              categoryFilter={state.category}
              conditionFilter={state.condition}
              countryFilter={state.country}
              manufacturerFilter={state.manufacturer}
              minPriceFilter={state.minPrice}
              maxPriceFilter={state.maxPrice}
              onSearchChange={(v) => handleFilterChange("q", v)}
              onPartNumberChange={(v) => handleFilterChange("partNumber", v)}
              onCategoryChange={(v) => handleFilterChange("category", v)}
              onConditionChange={(v) => handleFilterChange("condition", v)}
              onCountryChange={(v) => handleFilterChange("country", v)}
              onManufacturerChange={(v) => handleFilterChange("manufacturer", v)}
              onMinPriceChange={(v) => handleFilterChange("minPrice", v)}
              onMaxPriceChange={(v) => handleFilterChange("maxPrice", v)}
              onClearFilters={handleClearFilters}
              allInventoryItems={state.allInventoryItems}
              collapsed={state.filtersCollapsed}
              onToggleCollapse={() => setState((p) => ({ ...p, filtersCollapsed: !p.filtersCollapsed }))}
            />
          </aside>
        )}

        <main
          className={`${
            isTrulyEmptyInventory || isMobile
              ? "col-12"
              : state.filtersCollapsed
                ? "col-11"
                : "col-12 col-md-8 col-lg-9"
          } bg-white`}
        >
          <div className="p-3">
            {isTrulyEmptyInventory ? (
              <EmptyInventoryState
                onAddItem={() => setIsEditOpen(true)}
                onUploadFile={() => fileInputRef.current?.click()}
                onDownloadTemplate={handleDownloadTemplate}
              />
            ) : (
              <>
                {/* [2026-02-10] Mobile: Render card list; Desktop: Render grid */}
                {isMobile ? (
                  <div className="mobile-list-container">
                    {state.loading ? (
                      <div className="text-center py-4">Loading...</div>
                    ) : state.error ? (
                      <div className="text-center py-4 text-danger">{state.error}</div>
                    ) : state.items.length === 0 ? (
                      <div className="text-center py-4">No items found</div>
                    ) : (
                      state.items.map((item) => (
                        <MobileInventoryCard
                          key={item._id}
                          item={item}
                          onEdit={async (it) => {
                            const { data } = await getInventoryItem(it._id!);
                            setEditInitial(data);
                            setIsEditOpen(true);
                          }}
                          onDelete={(it) => setDeleteConfirmItem(it)}
                          onClick={(it) => navigate(`/inventory/item/${it._id}`)}
                          isDeleting={isDeletingId === item._id}
                        />
                      ))
                    )}
                  </div>
                ) : (
                  <InventoryGrid
                    items={state.items}
                    loading={state.loading}
                    error={state.error}
                    total={state.total}
                    currentPage={state.page}
                    totalPages={state.pages}
                    onRetry={() => fetchItems(state.page)}
                    displayMode="list"
                    paginationMode={true}
                    hasMore={false}
                    onLoadMore={() => {}}
                    renderItem={(item: Inventory) => (
                      <InventoryItemCard
                        item={item}
                        onEdit={async (it: Inventory) => {
                          const { data } = await getInventoryItem(it._id!);
                          setEditInitial(data);
                          setIsEditOpen(true);
                        }}
                        onDelete={(it: Inventory) => setDeleteConfirmItem(it)}
                        onClick={(it: Inventory) => navigate(`/inventory/item/${it._id}`)}
                        displayMode="row"
                        isDeleting={isDeletingId === item._id}
                      />
                    )}
                  />
                )}
                {state.pages > 0 && (
                  <PaginationControls
                    currentPage={state.page}
                    totalPages={state.pages}
                    itemsPerPage={state.itemsPerPage}
                    totalItems={state.total}
                    onPageChange={(p) => setState((prev) => ({ ...prev, page: p }))}
                    onItemsPerPageChange={(limit) => setState((prev) => ({ ...prev, itemsPerPage: limit, page: 1 }))}
                    loading={state.loading}
                  />
                )}
              </>
            )}
          </div>
        </main>
      </div>

      <EditInventoryModal
        show={isEditOpen}
        onHide={() => { setIsEditOpen(false); setEditInitial(null); }}
        initialItem={editInitial}
        onSaved={async () => setState((p) => ({ ...p, page: 1 }))}
        // [2026-02-13] CURRENCY_PHASE1: Pass user's preferred currency for new items
        userCurrency={(profile?.preferredCurrency as any) || "USD"}
      />

      <InventoryUploadModals
        showUploadConfirm={showUploadConfirm}
        onHide={() => cancelUpload(fileInputRef)}
        onConfirm={() => confirmUpload(fileInputRef)}
        isUploading={isUploading}
      />

      <InventoryDeleteModal
        deleteConfirmItem={deleteConfirmItem}
        onHide={() => setDeleteConfirmItem(null)}
        onConfirm={() => handleDeleteConfirm(deleteConfirmItem!, setIsDeletingId, setDeleteConfirmItem)}
        isDeleting={!!isDeletingId}
      />
    </div>
  );
};

export default InventoryPage;
